"use client";

import type React from "react";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
// import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Star,
  Quote,
  Users,
  UserRound,
  GraduationCapIcon,
  Heart,
  Send,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { container, item } from "@/lib/framer";
import { testimonialSchema, TestimonialFormValues } from "@/lib/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  FormField,
  FormLabel,
  FormMessage,
  FormItem,
  FormControl,
  Form,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";

// Define testimonial type
type Testimonial = {
  id: number;
  name: string;
  role: string;
  image: string;
  quote: string;
  rating: number;
  category: "parent" | "student" | "alumni" | "staff";
  year: number;
  featured?: boolean;
};

const API_URL = "/api";

export default function TestimonialsPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  // const featuredRef = useRef(null);
  // const isFeaturedInView = useInView(featuredRef, { once: true, amount: 0.2 });

  const form = useForm<TestimonialFormValues>({
    resolver: zodResolver(testimonialSchema as any),
    defaultValues: {
      name: "",
      relationship: "parent",
      role: "",
      email: "",
      testimonial: "",
      consent: false,
      rating: undefined,
      year: 2023,
    },
  });

  const handleSubmitTestimonial = async (data: TestimonialFormValues) => {
    console.log("Testimonial Data:", data);

    // Simulate API call
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/contact/testimonial`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        toast.success("Testimonial submitted successfully!");
        setFormSubmitted(true);
        form.reset();
      } else {
        toast.error(
          "An error occurred while submitting the testimonial. Please try again."
        );
      }
    } catch (error) {
      console.error("Error submitting testimonial:", error);
      toast.error(
        "An error occurred while submitting the testimonial. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (rating: number) => {
    form.setValue("rating", rating);
  };

  // Testimonials data
  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: "Aarav Sharma",
      role: "Parent of Grade 3 student",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "The teachers at Shree Janakalyan Boarding School are exceptional. My son has developed a love for reading and mathematics that I never expected at such a young age. The school's focus on holistic development has helped him grow not just academically but also socially and emotionally.",
      rating: 5,
      category: "parent",
      year: 2023,
      featured: true,
    },
    {
      id: 2,
      name: "Priya Tamang",
      role: "Parent of Grade 5 student",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "I appreciate how the school balances academic excellence with cultural education. My daughter is learning about our Nepali heritage while also developing global perspectives. The extracurricular activities offered have helped her discover her talents in music and dance.",
      rating: 5,
      category: "parent",
      year: 2022,
    },
    {
      id: 3,
      name: "Rajesh Gurung",
      role: "Parent of Grade 1 student",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "The safe and nurturing environment at Shree Janakalyan Boarding School has made the transition to school life smooth for my child. The staff truly cares about each student's wellbeing. I've seen remarkable progress in my child's confidence and social skills since joining the school.",
      rating: 4,
      category: "parent",
      year: 2023,
    },
    {
      id: 4,
      name: "Sunita Poudel",
      role: "Parent of Grade 4 student",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "The extracurricular activities offered by the school have helped my daughter discover her talents in art and music. The holistic approach to education is impressive. Teachers provide regular feedback and are always available to discuss my child's progress.",
      rating: 5,
      category: "parent",
      year: 2022,
      featured: true,
    },
    {
      id: 5,
      name: "Bikash Thapa",
      role: "Parent of Grade 2 student",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "Communication between teachers and parents is excellent. I always feel informed about my child's progress and any upcoming events or requirements. The school's use of technology to facilitate learning has been especially beneficial during challenging times.",
      rating: 5,
      category: "parent",
      year: 2023,
    },
    {
      id: 6,
      name: "Meena Shrestha",
      role: "Parent of Grade 6 student",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "My son has been at Shree Janakalyan since Grade 1, and the transformation in his confidence and academic abilities has been remarkable. The school truly nurtures potential. The teachers' dedication to helping each child succeed is evident in their personalized approach to education.",
      rating: 5,
      category: "parent",
      year: 2021,
    },
    {
      id: 7,
      name: "Anish Rai",
      role: "Grade 6 Student",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "I love coming to school every day because my teachers make learning fun and interesting. The science lab is my favorite place because we get to do cool experiments. I've made many friends here and enjoy participating in sports and cultural activities.",
      rating: 5,
      category: "student",
      year: 2023,
      featured: true,
    },
    {
      id: 8,
      name: "Sita Adhikari",
      role: "Grade 5 Student",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "My favorite subjects are mathematics and art. Our teachers explain things in a way that makes it easy to understand. I like that we have both indoor and outdoor activities. The annual cultural program is something I look forward to every year.",
      rating: 5,
      category: "student",
      year: 2023,
    },
    {
      id: 9,
      name: "Rohan Magar",
      role: "Grade 4 Student",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "School is fun because we learn new things every day. I like using computers in the computer lab and reading books in the library. Our teachers are kind and help us when we don't understand something. I enjoy playing football during sports time.",
      rating: 4,
      category: "student",
      year: 2022,
    },
    {
      id: 10,
      name: "Deepak Karki",
      role: "Alumni, Graduated 2015",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "The foundation I received at Shree Janakalyan Boarding School has been instrumental in my academic and professional journey. The values of discipline, curiosity, and perseverance that were instilled in me during my formative years continue to guide me. I'm now pursuing a career in engineering and am grateful for the strong mathematical and scientific foundation I received.",
      rating: 5,
      category: "alumni",
      year: 2022,
      featured: true,
    },
    {
      id: 11,
      name: "Sabina Gurung",
      role: "Alumni, Graduated 2018",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "My years at Shree Janakalyan were some of the best of my life. The teachers not only focused on academic excellence but also encouraged us to pursue our passions. The confidence I gained through participating in debates and cultural programs has helped me tremendously in college. The school's emphasis on both traditional values and modern education prepared me well for the future.",
      rating: 5,
      category: "alumni",
      year: 2023,
    },
    {
      id: 12,
      name: "Ramesh Bhattarai",
      role: "Alumni, Graduated 2010",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "Looking back, I realize how fortunate I was to attend Shree Janakalyan. The school's holistic approach to education helped me develop not just academically but as a well-rounded individual. The friendships I formed and the lessons I learned continue to influence my life. I'm proud to see how the school has grown and improved over the years while maintaining its core values.",
      rating: 5,
      category: "alumni",
      year: 2021,
    },
    {
      id: 13,
      name: "Sarita Thapa",
      role: "Science Teacher, 10 years at SJBS",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "Teaching at Shree Janakalyan has been a rewarding experience. The school's commitment to providing quality education while nurturing each child's unique abilities creates an ideal learning environment. The collaborative atmosphere among staff and the support from administration allow us to implement innovative teaching methods. Watching students grow and develop their potential is the greatest reward of being an educator here.",
      rating: 5,
      category: "staff",
      year: 2023,
      featured: true,
    },
    {
      id: 14,
      name: "Nabin Shrestha",
      role: "Mathematics Teacher, 8 years at SJBS",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "What sets Shree Janakalyan apart is the genuine care for both students and staff. The school invests in teacher development, which enables us to continually improve our teaching practices. The small class sizes allow for personalized attention to each student. I've seen countless students develop a love for mathematics through our hands-on, practical approach to teaching the subject.",
      rating: 5,
      category: "staff",
      year: 2022,
    },
    {
      id: 15,
      name: "Kamala Poudel",
      role: "Administrative Staff, 12 years at SJBS",
      image: "/placeholder.svg?height=100&width=100",
      quote:
        "Having been part of the Shree Janakalyan family for over a decade, I've witnessed the school's growth and evolution. What remains constant is the commitment to providing a nurturing environment where children can thrive. The sense of community among staff, students, and parents makes this more than just a workplace. It's gratifying to contribute to an institution that has such a positive impact on children's lives.",
      rating: 4,
      category: "staff",
      year: 2021,
    },
  ];

  // Filter testimonials based on active category and search query
  const filteredTestimonials = testimonials.filter((testimonial) => {
    const matchesCategory =
      activeCategory === "all" || testimonial.category === activeCategory;
    const matchesSearch =
      testimonial.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      testimonial.quote.toLowerCase().includes(searchQuery.toLowerCase()) ||
      testimonial.role.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get featured testimonials
  const featuredTestimonials = testimonials.filter(
    (testimonial) => testimonial.featured
  );

  return (
    <div className="min-h-screen flex flex-col">
      <section className="relative bg-muted py-8 md:py-10">
        <div className="container mx-auto px-4">
          <div className="mb-6">
            <Link
              href="/"
              className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </div>
          <div className="grid gap-8 md:grid-cols-2 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl font-bold tracking-tight mb-4">
                What Our Community Says
              </h1>
              <p className="text-muted-foreground mb-6">
                Discover what parents, students, alumni, and staff have to say
                about their experiences at Shree Janakalyan Boarding School.
                These testimonials reflect our commitment to providing quality
                education and nurturing each child&apos;s potential.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild>
                  <a href="#testimonials">Read Testimonials</a>
                </Button>
                <Button variant="outline" asChild>
                  <a href="#share-experience">Share Your Experience</a>
                </Button>
              </div>
            </motion.div>
            <motion.div
              className="relative h-[300px] md:h-[400px] overflow-hidden rounded-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Image
                src="https://res.cloudinary.com/ds1npuv1v/image/upload/v1742045087/anthem_hmqfrs.jpg"
                alt="Happy students and teachers"
                fill
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent flex items-end p-6">
                <p className="text-white text-lg font-medium">
                  Singing National Anthem
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Testimonials Section */}
      <section className="py-16 md:py-24" id="testimonials">
        <div className="container mx-auto px-4">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Featured Testimonials
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Hear from members of our school community who have shared their
              experiences and stories.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2">
            {featuredTestimonials.slice(0, 2).map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 50 }}
                animate={
                  // isFeaturedInView
                  // ? { opacity: 1, y: 0 }
                  { opacity: 1, y: 0 }
                }
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <Card className="h-full overflow-hidden">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-6">
                      {/* <div className="relative h-16 w-16 rounded-full overflow-hidden flex-shrink-0">
                          <Image
                            src={testimonial.image || "/placeholder.svg"}
                            alt={testimonial.name}
                            fill
                            className="object-cover"
                          />
                        </div> */}
                      <div>
                        <h3 className="font-bold text-lg">
                          {testimonial.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {testimonial.role}
                        </p>
                        <div className="flex mt-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${
                                i < testimonial.rating
                                  ? "text-accent fill-accent"
                                  : "text-gray-300"
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="relative">
                      <Quote className="absolute -top-2 -left-2 h-8 w-8 text-primary/10" />
                      <p className="text-muted-foreground relative z-10 pl-4">
                        {testimonial.quote}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* All Testimonials Section */}
      <section id="testimonials" className="py-16 md:py-24 bg-muted">
        <div className="container mx-auto px-4">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              All Testimonials
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Browse through testimonials from different members of our school
              community.
            </p>
          </motion.div>

          {/* Search and Filter */}
          {/* <div className="mb-8">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="relative w-full md:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search testimonials..."
                    className="w-full pl-10 pr-4 py-2 rounded-md border border-input bg-background"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <Tabs defaultValue="all" className="w-full md:w-auto" onValueChange={setActiveCategory}>
                  <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full md:w-auto">
                    <TabsTrigger value="all" className="text-xs md:text-sm">
                      All
                    </TabsTrigger>
                    <TabsTrigger value="parent" className="text-xs md:text-sm">
                      Parents
                    </TabsTrigger>
                    <TabsTrigger value="student" className="text-xs md:text-sm">
                      Students
                    </TabsTrigger>
                    <TabsTrigger value="alumni" className="text-xs md:text-sm">
                      Alumni
                    </TabsTrigger>
                    <TabsTrigger value="staff" className="text-xs md:text-sm">
                      Staff
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div> */}

          {/* Testimonials Grid */}
          <motion.div
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            {filteredTestimonials.length > 0 ? (
              filteredTestimonials.map((testimonial) => (
                <motion.div
                  key={testimonial.id}
                  variants={item}
                  whileHover={{ y: -5 }}
                  className="h-full"
                >
                  <Card className="h-full overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        {/* <div className="relative h-12 w-12 rounded-full overflow-hidden flex-shrink-0">
                            <Image
                              src={testimonial.image || "/placeholder.svg"}
                              alt={testimonial.name}
                              fill
                              className="object-cover"
                            />
                          </div> */}
                        <div>
                          <h3 className="font-bold">{testimonial.name}</h3>
                          <p className="text-xs text-muted-foreground">
                            {testimonial.role}
                          </p>
                          <div className="flex mt-1">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star
                                key={i}
                                className={`h-3 w-3 ${
                                  i < testimonial.rating
                                    ? "text-yellow-500 fill-yellow-500"
                                    : "text-gray-300"
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="relative">
                        <Quote className="absolute -top-2 -left-2 h-6 w-6 text-primary/10" />
                        <p className="text-sm text-muted-foreground relative z-10 pl-2">
                          {testimonial.quote}
                        </p>
                      </div>
                      <div className="mt-4 text-xs text-right text-muted-foreground">
                        Shared in {testimonial.year}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full py-12 text-center">
                <p className="text-muted-foreground">
                  No testimonials found matching your search criteria.
                </p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    setSearchQuery("");
                    setActiveCategory("all");
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Testimonial Categories Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Our School Community
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Explore testimonials from different members of our school
              community.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: <Users className="h-8 w-8 text-primary" />,
                title: "Parents",
                description:
                  "Hear from parents about their children's growth and development at Shree Janakalyan Boarding School.",
                count: testimonials.filter((t) => t.category === "parent")
                  .length,
                category: "parent",
              },
              {
                icon: <UserRound className="h-8 w-8 text-primary" />,
                title: "Students",
                description:
                  "Our students share their experiences, favorite subjects, and activities at our school.",
                count: testimonials.filter((t) => t.category === "student")
                  .length,
                category: "student",
              },
              {
                icon: <GraduationCapIcon className="h-8 w-8 text-primary" />,
                title: "Alumni",
                description:
                  "Former students reflect on how their education at our school has shaped their lives and careers.",
                count: testimonials.filter((t) => t.category === "alumni")
                  .length,
                category: "alumni",
              },
              {
                icon: <Heart className="h-8 w-8 text-primary" />,
                title: "Staff",
                description:
                  "Our dedicated teachers and staff share their experiences working at Shree Janakalyan Boarding School.",
                count: testimonials.filter((t) => t.category === "staff")
                  .length,
                category: "staff",
              },
            ].map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <Card className="h-full">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      {category.icon}
                    </div>
                    <h3 className="text-xl font-bold mb-2">{category.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4 flex-grow">
                      {category.description}
                    </p>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">
                        {category.count} testimonials
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-primary"
                        onClick={() => {
                          // setActiveCategory(category.category)
                          document
                            .getElementById("testimonials")
                            ?.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        View All
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Share Your Experience Section */}
      <section id="share-experience" className="py-16 md:py-24 bg-muted">
        <div className="container mx-auto px-4">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Share Your Experience
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We value your feedback and would love to hear about your
              experience with Shree Janakalyan Boarding School. Your testimonial
              helps others learn about our school community.
            </p>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="p-6">
                {formSubmitted ? (
                  <motion.div
                    className="text-center py-8"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ type: "spring", damping: 15 }}
                  >
                    <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
                      <CheckCircle2 className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4">
                      Thank You for Sharing!
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Your testimonial has been submitted successfully. We
                      appreciate you taking the time to share your experience
                      with our school community.
                    </p>
                    <Button onClick={() => setFormSubmitted(false)}>
                      Share Another Experience
                    </Button>
                  </motion.div>
                ) : (
                  <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit(handleSubmitTestimonial)}
                      className="space-y-6"
                    >
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Your Name</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Enter your name"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="relationship"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Relationship to School</FormLabel>
                                <FormControl>
                                  <select
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    {...field}
                                  >
                                    <option value="">
                                      Select your relationship
                                    </option>
                                    <option value="parent">Parent</option>
                                    <option value="student">Student</option>
                                    <option value="alumni">Alumni</option>
                                    <option value="staff">Staff</option>
                                    <option value="other">Other</option>
                                  </select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <FormField
                          control={form.control}
                          name="role"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Your Role/Position</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="E.g., Parent of Grade 3 student, Grade 5 Student, etc."
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email Address</FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="Enter your email address"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                              <p className="text-xs text-muted-foreground">
                                Your email will not be published. It&apos;s only
                                for verification purposes.
                              </p>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="testimonial"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Your Experience</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Share your experience with Shree Janakalyan Boarding School..."
                                  rows={5}
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="space-y-2">
                          <Label>Rating</Label>
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((rating) => (
                              <button
                                title={`Rate ${rating} stars`}
                                key={rating}
                                type="button"
                                className="text-gray-300 hover:text-accent focus:outline-none"
                                onClick={() => handleRatingChange(rating)}
                              >
                                <Star className="h-6 w-6 fill-current" />
                              </button>
                            ))}
                          </div>
                        </div>

                        <FormField
                          control={form.control}
                          name="consent"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  I consent to having my testimonial displayed
                                  on the school website. I understand that my
                                  email address will not be published.
                                </FormLabel>
                                <FormMessage />
                              </div>
                            </FormItem>
                          )}
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full flex items-center gap-2"
                        disabled={!form.formState.isValid || loading}
                      >
                        {loading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        {loading ? "Submitting..." : "Submit Testimonial"}
                      </Button>
                    </form>
                  </Form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Join Our School Community
            </h2>
            <motion.p
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Experience the nurturing environment and quality education that
              our community members have shared in their testimonials.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Button size="lg" variant="secondary" asChild>
                <Link href="/online-admission">Apply for Admission</Link>
              </Button>
              <Button size="lg" variant="secondary" asChild>
                <Link href="/contact">Contact Us</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
